import {
	ArrayField,
	arrayfield,
	btn,
	checkbox,
	comp,
	containerfield,
	EntityID,
	Field,
	fieldset,
	form,
	Form,
	h4,
	select,
	SelectField,
	t,
	tbar,
	textfield,
	Window
} from "@intermesh/goui";
import {Board, boardDS, Column, ColumnStatusMap, projectbookDS} from "./Index";
import {modules} from "@intermesh/groupoffice-core";
import {statuses} from "@intermesh/business-projects3";


// Copied from (goui) tasks module, can be imported from it when that branch is merged
export enum TaskProgressType {
	'needs-action' = 'Needs action',
	'in-progress' = 'In progress',
	'completed' = 'Completed',
	'failed' = 'Failed',
	'cancelled' = 'Cancelled'
}

export class ColumnDialog extends Window {
	readonly form: Form;
	private columnId: EntityID | undefined;
	private readonly board: Board;
	private statusMapsHiddenField: ArrayField;
	private readonly statusMapFields?: SelectField[];

	constructor(board: Board) {
		super();

		this.board = board;

		this.title = t("Column");
		this.height = 600;
		this.width = 650;
		this.modal = true;
		this.resizable = true;
		this.maximizable = false;
		this.stateId = "kanban-column-dialog";

		this.statusMapFields = [];

		if (modules.isAvailable("community", "tasks")) {
			let options = [];

			options.push(...Object.keys(TaskProgressType)
				.filter(key => isNaN(Number(key)))
				.map(key => ({
					value: key,
					name: TaskProgressType[key as keyof typeof TaskProgressType],
					saveTo: "status"
				}))
			);

			this.statusMapFields.push(this.buildStatusMapSelectField(t("Status for tasks"), "Task", options));
		}

		if (modules.isAvailable("business", "support")) {
			let options = [];

			options.push(...Object.keys(TaskProgressType)
				.filter(key => isNaN(Number(key)))
				.map(key => ({
					value: key,
					name: TaskProgressType[key as keyof typeof TaskProgressType],
					saveTo: "status"
				}))
			);

			this.statusMapFields.push(this.buildStatusMapSelectField(t("Status for support tickets"), "SupportTicket", options));
		}

		if (modules.isAvailable("business", "projects3")) {
			let standardOptions: any[] = [];

			Object.values(statuses).forEach((status: any) => {
				standardOptions.push({
					value: status.id,
					name: status.name,
					saveTo: "status"
				})
			});

			this.statusMapFields.push(this.buildStatusMapSelectField(t("Default status for projects"), "Project3", standardOptions));

			this.on("beforerender", async ({target}) => {
				const response = await projectbookDS.query({
					filter: {
						hasStatuses: true
					}
				});

				if (response.ids) {
					const books = await projectbookDS.get(response.ids);

					if (books) {
						books.list.forEach((book) => {
							const options: any[] = [];

							book.statuses.forEach((status: any) => {
								options.push({
									value: status.id,
									name: status.name,
									saveTo: "statusId"
								});
							});

							target.findChild("statusmapfields")!.items.add(this.buildStatusMapSelectField(t("Status for projects in") + " " + book.name, "Project3", options));
						});
					}
				}
			});
		}

		this.form = form({
				flex: 1,
				cls: "vbox",
				handler: async (form) => {
					if (this.board.id) {
						const value = form.value;
						const updateColumns = this.board.columns;

						if (this.columnId) {
							if (form.value.isFinal) {
								updateColumns.forEach((c) => {
									c.isFinal = false;
								});
							}

							const current = updateColumns.find((c: Column) => c.id === this.columnId);
							Object.assign(current!, value);
						} else {
							value.boardId = this.board.id;
							value.sortOrder = this.board.columns.length;

							updateColumns.push(value as Column);
						}

						await boardDS.update(this.board.id, {columns: updateColumns});
						this.close();
					}
				}
			},
			fieldset({
					cls: "flow scroll",
					flex: 1
				},
				textfield({
					label: t("Label"),
					name: "label"
				}),
				h4({text: t("Status mapping")}),
				this.statusMapsHiddenField = arrayfield({
					name: "statusMaps",
					buildField: () => {
						return containerfield({hidden: true})
					}
				}),
				comp({itemId: "statusmapfields"},
					...this.statusMapFields
				),
				checkbox({
					label: t("Is final column, cards placed in this column will be removed after retention period."),
					name: "isFinal"
				})
			),
			tbar({}, "->", btn({type: "submit", text: t("OK")}))
		);

		this.items.add(this.form);
	}

	public load(column: any) {
		this.title = column.label;
		this.columnId = column.id;
		this.form.value = column;
	}

	private buildStatusMapSelectField(label: string, childEntity: string, options: Array<any>) {
		return select({
				label: label,
				style: {paddingBottom: "1.4rem"},
				buttons: [
					btn({
						icon: "clear",
						type: "button",
						handler: (btn) => {
							(btn.parent!.parent! as Field).clear();
						}
					})
				],
				options: options,
				listeners: {
					render: ({target}) => {
						for (const statusMap of this.form.value.statusMaps) {
							if (statusMap.entity != childEntity) {
								continue;
							}

							const saveTo = statusMap.status ? "status" : "statusId"

							if (saveTo == "status") {
								target.value = statusMap.status;
							} else {
								const matching = target.options.find((o) => o.value == statusMap.statusId);

								if (matching) {
									target.value = statusMap.statusId;
								}
							}
						}
					},
					setvalue: ({newValue, target}) => {
						if (!newValue) {
							const statusMaps = this.statusMapsHiddenField.value;
							this.statusMapsHiddenField.value = statusMaps.filter((e: Record<string, any>) => e.entity !== childEntity);
							return;
						}

						if (newValue) {
							const saveTo = target.options.find((o: any) => o.value == newValue)!.saveTo;

							const statusMap: ColumnStatusMap = {
								columnId: this.columnId ?? undefined,
								entity: childEntity,
								[saveTo]: newValue,
								boardId: this.board.id
							}

							const statusMaps = this.statusMapsHiddenField.value;

							if (saveTo == "status") {

								const index = this.statusMapsHiddenField.value.findIndex(e => e.entity == childEntity && e.status !== null);
								if (index !== -1) {

									statusMaps.splice(index, 1, statusMap);

									this.statusMapsHiddenField.value = statusMaps;

									return;
								}
							} else {
								const index = statusMaps.findIndex(e =>
									e.entity == childEntity &&
									target.options.some((o: any) => o.value == e.statusId)
								);

								if (index !== -1) {
									statusMaps.splice(index, 1, statusMap);
									this.statusMapsHiddenField.value = statusMaps;
									return;
								}
							}


							this.statusMapsHiddenField.addValue(statusMap);
						}
					}
				}
			}
		);
	}
}